import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import NewsForm from "../../components/NewsForm";
import { localizedTextDefaultValue } from "../../constants/localizedTextDefaultValue";
import { LocalizedText, News } from "../../services/generated/client";
import {
  createNews,
  getAllNews,
  removeNews,
  updateNews,
} from "../../services/news/newsServices";
import styles from "./styles.module.css";
import { MdRemoveRedEye, MdOutlineModeEdit, MdDelete } from "react-icons/md";
import { useApi } from "../../services/api";

const NewsPage = () => {
  const { withErrorHandling } = useApi();
  const [news, setNews] = useState<News[]>([]);
  const [selectedNews, setSelectedNews] = useState<News>({
    _id: {},
    title: localizedTextDefaultValue,
    date: "",
    mainImageUrl: "",
    content: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState(false);
  const [languageEditing, setLanguageEditing] =
    useState<keyof LocalizedText>("ka");

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    withErrorHandling(async () => {
      const response = await getAllNews();
      if (response) {
        setNews(response);
      }
    });
  }, []);

  const handleEdit = (news: News) => {
    setSelectedNews(news);
    setIsAdding(false);
    setView(false);
    setIsModalOpen(true);
  };

  const handleView = (news: News) => {
    setSelectedNews(news);
    setIsAdding(false);
    setView(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (_id: string, title: string) => {
    if (window.confirm(`Are you sure you want to delete ${title}?`)) {
      await removeNews(_id);
      setNews((prev) => prev.filter((news) => news._id.toString() !== _id));
    }
  };

  const handleUpdateNews = async (news: News) => {
    const { _id, title, date, mainImageUrl, content } = news;
    const payload = { title, date, mainImageUrl, content };

    await withErrorHandling(async () => {
      if (isAdding) {
        const newNews = await createNews(payload);
        setNews((prev) => [...prev, newNews]);
      } else {
        await updateNews(_id.toString(), payload);
        setNews((prev) =>
          prev.map((newsItem) => (newsItem._id === _id ? news : newsItem))
        );
        alert("News updated successfully!");
      }
      setIsModalOpen(false);
    });
  };

  const handleAddNews = () => {
    setSelectedNews({
      _id: {},
      title: localizedTextDefaultValue,
      date: "",
      mainImageUrl: "",
      content: [],
    });
    setView(false);
    setIsAdding(true);
    setIsModalOpen(true);
  };

  const columns = useMemo<MRT_ColumnDef<News>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ row }) => row.original.title?.[languageEditing] || "",
      },

      {
        header: "Actions",
        Cell: ({ row }: { row: { original: News } }) => {
          return (
            <div className={styles.actionBtns}>
              <button
                onClick={() => handleView(row.original)}
                className={styles.tableBtn}
              >
                <MdRemoveRedEye />
              </button>
              <button
                onClick={() => handleEdit(row.original)}
                className={styles.tableBtn}
              >
                <MdOutlineModeEdit />
              </button>
              <button
                onClick={() =>
                  handleDelete(
                    row.original._id.toString(),
                    row.original.title.en
                  )
                }
                className={`${styles.tableBtn} ${styles.delete}`}
              >
                <MdDelete />
              </button>
            </div>
          );
        },
      },
    ],
    [languageEditing]
  );

  return (
    <div className={styles.mainContent}>
      <h2>Manage News</h2>
      <div className={styles.tableWrapper}>
        <MaterialReactTable
          columns={columns}
          data={Array.isArray(news) ? news : []}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", gap: "20px" }}>
              <button className={styles.addBtn} onClick={handleAddNews}>
                + Add News
              </button>
              <div>
                <LanguageSwitcher
                  languageEditing={languageEditing}
                  setLanguageEditing={setLanguageEditing}
                  inForm={false}
                />
              </div>
            </div>
          )}
          muiTableContainerProps={{
            sx: {
              flex: 1,
              overflowY: "hidden",
            },
          }}
          muiTablePaperProps={{
            sx: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableBodyProps={{
            sx: {
              overflowY: "auto",
              height: "100%",
            },
          }}
          muiTableProps={{
            sx: {
              maxHeight: "100%",
              gridTemplateRows: "auto 1fr",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              overflow: "auto",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          layoutMode="grid"
        />
      </div>

      {isModalOpen && selectedNews && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} ref={modalRef}>
            <button
              className={styles.closeButton}
              onClick={() => setIsModalOpen(false)}
            >
              X
            </button>
            <LanguageSwitcher
              languageEditing={languageEditing}
              setLanguageEditing={setLanguageEditing}
              inForm={true}
            />
            <NewsForm
              isAdding={isAdding}
              news={selectedNews}
              onSubmit={handleUpdateNews}
              isView={view}
              language={languageEditing}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
